import type { AnalyticsProvider } from '~analytics/types/analyticsProvider';
import type { RuntimeConfig } from 'nuxt/schema';

const MOCK_ANALYTICS_PROVIDERS_KEY = '__mockAnalyticsProviders';

export default class MockProvider implements AnalyticsProvider {
  serviceName: string;
  subscribedEvents: string[];

  constructor(
    serviceName: string,
    config: RuntimeConfig,
    subscribedEvents: string[],
  ) {
    this.serviceName = serviceName;
    this.subscribedEvents = subscribedEvents;
  }

  init() {
    // eslint-disable-next-line no-console
    console.log('Analytics mock provider initialised', this.serviceName);

    // MockProvider is used only when analytics are disabled (dev / E2E) — a correctly
    // configured production build runs the real providers instead, so this window hook
    // is not created there. Record each registration on `window` so E2E can assert
    // which providers the suppression gate allowed to register — a deterministic
    // signal, unlike the console `track()` log which races registration under CI
    // (see apps/phoenix/playwright/tests/unified/westpacCashoutLanding.spec.ts).
    if (typeof window !== 'undefined') {
      const registry = window as unknown as Record<string, string[] | undefined>;
      (registry[MOCK_ANALYTICS_PROVIDERS_KEY] ??= []).push(this.serviceName);
    }
  }

  logMockCall(functionName: string, args: object) {
    // eslint-disable-next-line no-console
    console.log([this.serviceName, functionName, args]);
  }

  track(eventName: string, eventData?: object) {
    this.logMockCall(eventName, eventData || {});
  }

  identify(guid: string | undefined) {
    this.logMockCall('identify', { guid });
  }

  reset() {
    this.logMockCall('reset', {});
  }

  setUserProperties(properties: Record<string, unknown>) {
    this.logMockCall('setUserProperties', properties);
  }

  getSubscribedEvents() {
    return this.subscribedEvents;
  }
}
